import { useEffect, useState } from "react"
import CMSData from "aldoo-ra/CMS/cms-data"
import { updateCDNUrls } from "aldoo-ra/HtmlEditor/cdn-urls"
import parse from "html-react-parser"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

/**
 * Clean up editor-specific classes and normalize event handlers from HTML content
 * @param {string} htmlString - HTML string to clean
 * @returns {string} Cleaned HTML string
 */
const cleanUpEditorClasses = (htmlString) => {
  if (!htmlString) return ""

  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return htmlString
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, "text/html")

  // Find all elements with editor classes
  const elementsWithEditorClasses = doc.querySelectorAll('[class*="editor-"]')

  elementsWithEditorClasses.forEach((element) => {
    const classList = Array.from(element.classList)
    // Remove all classes that start with "editor-"
    classList.forEach((className) => {
      if (className.startsWith("editor-")) {
        element.classList.remove(className)
      }
    })

    // If classList is now empty, remove the class attribute entirely
    if (element.classList.length === 0) {
      element.removeAttribute("class")
    }
  })

  // Remove lowercase event handler attributes (onmouseover, onmouseout, etc.)
  // These cause React warnings and don't work properly in React
  const allElements = doc.body.querySelectorAll("*")
  const lowercaseEventHandlers = [
    'onmouseover', 'onmouseout', 'onclick', 'onload', 'onerror',
    'onmousedown', 'onmouseup', 'onmousemove', 'onkeydown', 'onkeyup',
    'onkeypress', 'onfocus', 'onblur', 'onchange', 'onsubmit'
  ]

  allElements.forEach((element) => {
    lowercaseEventHandlers.forEach((handler) => {
      if (element.hasAttribute(handler)) {
        element.removeAttribute(handler)
      }
    })
  })

  return doc.body.innerHTML
}

/**
 * HTMLContent component - Fetches and displays HTML content from CMS
 * @param {string} id - The content ID to fetch
 * @param {string} className - Optional className for the container
 * @param {string} variant - Variant name for styling
 * @param {object} styleOverrides - Style overrides for variants
 */
const HTMLContent = ({
  id,
  className = "",
  variant = "default",
  styleOverrides
}) => {
  const [htmlContent, setHtmlContent] = useState("")

  const variants = VariantReader.useVariant({
    componentName: "HTMLContent",
    variant,
    styleOverrides,
  })

  const { data, loading, error } = CMSData({
    contentType: "HTMLContent",
    match: { "content.id": id },
  })

  useEffect(() => {
    if (data && data.length > 0) {
      const content = data[0]

      // Get HTML content from content.content field (based on the example structure)
      const rawHTML = content.content || ""

      // Parse the original HTML to extract head elements (styles, scripts, meta tags)
      const originalParser = new DOMParser()
      const originalDoc = originalParser.parseFromString(rawHTML, "text/html")

      // Extract meta tags from original document
      const metaTags = originalDoc.head?.querySelectorAll("meta") || []
      metaTags.forEach((meta) => {
        const name = meta.getAttribute("name") || meta.getAttribute("property")
        if (name) {
          const existing = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`)
          if (!existing) {
            const newMeta = document.createElement("meta")
            Array.from(meta.attributes).forEach((attr) => {
              newMeta.setAttribute(attr.name, attr.value)
            })
            newMeta.setAttribute("data-html-content", id)
            document.head.appendChild(newMeta)
          }
        }
      })

      // Extract and inject style tags from original document
      const styleElements = originalDoc.head?.querySelectorAll("style") || []

      // Remove any existing styles for this content
      const existingStyles = document.querySelectorAll(`style[data-html-content="${id}"]`)
      existingStyles.forEach((style) => style.remove())

      // Inject styles directly into document head
      styleElements.forEach((styleEl) => {
        const newStyle = document.createElement("style")
        newStyle.textContent = styleEl.textContent
        newStyle.setAttribute("data-html-content", id)
        newStyle.setAttribute("type", "text/css")
        document.head.appendChild(newStyle)
      })

      // Extract and inject script tags from original document
      const scriptElements = originalDoc.querySelectorAll("head script, body script")
      scriptElements.forEach((script) => {
        const src = script.getAttribute("src")
        if (src) {
          const existing = document.querySelector(`script[src="${src}"]`)
          if (!existing) {
            const newScript = document.createElement("script")
            Array.from(script.attributes).forEach((attr) => {
              newScript.setAttribute(attr.name, attr.value)
            })
            newScript.setAttribute("data-html-content", id)
            document.head.appendChild(newScript)
          }
        } else if (script.textContent) {
          // Wrap inline scripts in try-catch to prevent errors from accessing null elements
          const newScript = document.createElement("script")
          newScript.textContent = `
            try {
              ${script.textContent}
            } catch (error) {
              console.warn('[HTMLContent] Script error:', error);
            }
          `
          newScript.setAttribute("data-html-content", id)
          // Defer execution to allow DOM to be ready
          newScript.defer = true
          document.head.appendChild(newScript)
        }
      })

      // Process body content with CDN URLs using updateCDNUrls
      let processedBodyHTML = updateCDNUrls(rawHTML)

      // Clean up editor classes
      processedBodyHTML = cleanUpEditorClasses(processedBodyHTML)

      setHtmlContent(processedBodyHTML)
    }

    // Cleanup function to remove injected elements when component unmounts
    return () => {
      if (id) {
        const injectedMetas = document.querySelectorAll(`meta[data-html-content="${id}"]`)
        injectedMetas.forEach((meta) => meta.remove())

        const injectedStyles = document.querySelectorAll(`style[data-html-content="${id}"]`)
        injectedStyles.forEach((style) => style.remove())

        const injectedScripts = document.querySelectorAll(`script[data-html-content="${id}"]`)
        injectedScripts.forEach((script) => script.remove())
      }
    }
  }, [data, id])

  if (loading) {
    return <div className={twMerge(variants.loading, className)}>Loading...</div>
  }

  if (error) {
    return <div className={twMerge(variants.error, className)}>Error loading content</div>
  }

  if (!data || data.length === 0) {
    return <div className={twMerge(variants.empty, className)}>Content not found</div>
  }
  return (
    <div className={twMerge(variants.container, className)}>
      {parse(htmlContent)}
    </div>
  )
}

// AldooComponent definition for auto-discovery
export const AldooComponent = {
  id: "HTMLContent",
  component: HTMLContent,
  description: "Fetches and displays HTML content from CMS by content ID. Automatically processes CDN URLs and injects styles.",
  input: {
    id: {
      type: "string",
      required: true,
      description: "The content ID to fetch from CMS",
    },
    className: {
      type: "string",
      required: false,
      description: "Optional CSS class name",
    },
    variant: {
      type: "string",
      required: false,
      description: "Variant name for styling (default, admin, article)",
    },
    styleOverrides: {
      type: "object",
      required: false,
      description: "Optional style overrides",
    },
  },
}

export default HTMLContent
